import { useEffect } from 'react';
import { ACTION_TYPES, usePurchaseContractSearchDispatch, usePurchaseContractSearchState } from './purchase-contract-search-context';
import PurchaseContractAppService from '../../../../application/platform/apm/purchase-contract/purchase-contract-app-service';
import { IPurchaseContractListResponse } from '../../../../model/apm/purchase-contract/response/purchase-contract-list-response';
import { IPurchaseContractAddEditRequest } from '../../../../model/apm/purchase-contract/request/purchase-contract-add-edit-request';
import { IPurchaseContractPagedRequest } from '../../../../model/apm/purchase-contract/request/purchase-contract-paged-request';
import { IPortalDocumentResponse } from '../../../../model/apm/document/portal-document-response';
import { parseNumberInput } from '../../../../utils/format';
import { useActionRunner } from '../../../../hooks/use-action-runner';
import { useSearchActions } from '../../../../hooks/use-search-actions';
import { useDocumentAttachments } from '../../components/use-document-attachments';
import { attachmentsRemovalNotice } from '../../components/document-utils';
import { parseBooleanFilter } from '../../../../utils/filter-options';

export const usePurchaseContractSearchController = () => {
    const dispatch = usePurchaseContractSearchDispatch();
    const state = usePurchaseContractSearchState();
    const { isSaving, runSaving, runWithLoading, run, showSuccess, confirmDelete } = useActionRunner();
    const { updateParameters, triggerSearch, ...searchHandlers } = useSearchActions<IPurchaseContractPagedRequest>(
        dispatch, ACTION_TYPES, { term: undefined, year: undefined, isCovid19: undefined },
    );
    const attachments = useDocumentAttachments({
        ownerId: state.drawer.contract?.id,
        files: state.drawer.files,
        setFiles: (files: IPortalDocumentResponse[]) => dispatch({ type: ACTION_TYPES.setDrawerFiles, payload: files }),
        upload: PurchaseContractAppService.uploadFile,
        remove: PurchaseContractAppService.removeFile,
        onChange: triggerSearch,
    });

    useEffect(() => {
        run('Não foi possível carregar os tipos de documento', async () => {
            dispatch({ type: ACTION_TYPES.setDocumentTypes, payload: await PurchaseContractAppService.listDocumentTypes() });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function handleOnChange(e: any) {
        const { name, value } = e.target;
        if (name === 'input-term') updateParameters({ term: value || undefined });
        if (name === 'input-year') updateParameters({ year: parseNumberInput(value) });
        if (name === 'select-covid') updateParameters({ isCovid19: parseBooleanFilter(value) });
    }

    // A listagem não traz discriminação, local de entrega e anexos, então a edição busca o contrato completo
    function openDrawer(row?: IPurchaseContractListResponse) {
        if (!row) {
            dispatch({ type: ACTION_TYPES.openDrawer });
            return;
        }
        runWithLoading('Não foi possível carregar o contrato', async () => {
            dispatch({ type: ACTION_TYPES.openDrawer, payload: await PurchaseContractAppService.getById(row.id) });
        });
    }

    function closeDrawer() {
        dispatch({ type: ACTION_TYPES.closeDrawer });
    }

    function handleSave(request: IPurchaseContractAddEditRequest) {
        const editing = state.drawer.contract;
        return runSaving('Não foi possível salvar o contrato', async () => {
            if (editing) {
                await PurchaseContractAppService.edit(editing.id, request);
                showSuccess('Contrato atualizado com sucesso!');
                closeDrawer();
            } else {
                // Mantém o drawer aberto, agora em edição, para anexar os documentos
                dispatch({ type: ACTION_TYPES.openDrawer, payload: await PurchaseContractAppService.add(request) });
                showSuccess('Contrato cadastrado! Agora anexe os documentos.');
            }
            triggerSearch();
        });
    }

    function handleDelete(row: IPurchaseContractListResponse) {
        const message = `Tem certeza que deseja excluir o contrato com "${row.contractedCompanyName}"?${attachmentsRemovalNotice(row.fileCount)}`;
        confirmDelete(message, () => run('Não foi possível excluir o contrato', async () => {
            await PurchaseContractAppService.remove(row.id);
            showSuccess('Contrato excluído com sucesso!');
            triggerSearch();
        }));
    }

    return {
        isSaving,
        handleOnChange, ...searchHandlers,
        openDrawer, closeDrawer, handleSave, handleDelete, ...attachments,
    };
};
